import { FilterOption, QuestionnaireAnswerType, QuestionnaireCategory, QuestionnaireQuestion } from '@/models/gen/graphql';

import { getRoles } from '@/api/services/filters/roles';
import { searchAnswerTypesWithMetadata } from '@/features/Settings/api/services/training/searchQuestionnaireAnswerTypes';
import { searchCategoriesWithMetadata } from '@/features/Settings/api/services/training/searchQuestionnaireCategories';
import { searchQuestionsWithMetadata } from '@/features/Settings/api/services/training/searchQuestionnaireQuestions';

export namespace TrainingQuestionsTypes {
  export type Filters = {
    text: string;
    roleId?: string;
    categoryId?: string;
  };
  export const initFilters: Filters = {
    text: '',
    roleId: undefined,
    categoryId: undefined,
  };
  export type Modals = {
    questionModal: {
      show: boolean;
      question?: QuestionnaireQuestion;
    } | null;
  };
  export const initModals: Modals = {
    questionModal: null,
  };
  export type Table = {
    sorting: {
      column?: string;
      direction?: 'asc' | 'desc';
    };
  };
  export const initTable: Table = {
    sorting: {},
  };
  export type State = {
    questions: QuestionnaireQuestion[];
    categories: QuestionnaireCategory[];
    answerTypes: QuestionnaireAnswerType[];
    roles: FilterOption[];
    total: number;
    loading: boolean;
    filters: Filters;
    modals: Modals;
    table: Table;
    fetch: () => Promise<void>;
    fetchRoles: () => Promise<void>;
    fetchCategories: () => Promise<void>;
    fetchAnswerTypes: () => Promise<void>;
    onReset: () => void;
    onSelectRole: (roleId: string) => void;
  };

  export const initState = (setState, getState) => {
    const fetch = async (): Promise<void> => {
      try {
        setState((current) => ({ ...current, loading: true }));
        const { state } = getState();
        const roleCategories = state.categories
          .filter((category) => category.roleId === state.filters.roleId)
          .map((category) => category.id);
        const selectedCategory = state.filters.categoryId;
        const categoryIds = selectedCategory ? [selectedCategory] : roleCategories;
        const { questions, totalCount } = await searchQuestionsWithMetadata(categoryIds, state.filters.text);
        setState((current) => ({ ...current, questions, total: totalCount }));
      } catch (err) {
        console.error('Error fetching questions:', err);
      } finally {
        setState((current) => ({ ...current, loading: false }));
      }
    };
    return {
      questions: [],
      categories: [],
      answerTypes: [],
      roles: [],
      total: 0,
      loading: false,
      filters: initFilters,
      modals: initModals,
      table: initTable,
      fetch,
      fetchRoles: async (): Promise<void> => {
        try {
          setState((current) => ({ ...current, loading: true }));
          const roles = await getRoles();
          setState((current) => ({ ...current, roles }));
        } finally {
          setState((current) => ({ ...current, loading: false }));
        }
      },
      fetchCategories: async (): Promise<void> => {
        const { categories } = await searchCategoriesWithMetadata({});
        setState((current) => ({ ...current, categories: categories || [] }));
      },
      fetchAnswerTypes: async (): Promise<void> => {
        const { answerTypes } = await searchAnswerTypesWithMetadata();
        setState((current) => ({ ...current, answerTypes: answerTypes || [] }));
      },
      onReset: (): void => {
        setState((current) => ({ ...current, filters: { ...initFilters, roleId: current.filters.roleId } }));
        fetch();
      },
      onSelectRole: (roleId: string): void => {
        // The category filter belongs to whichever role was active when it was picked
        setState((current) => ({ ...current, filters: { ...current.filters, roleId, categoryId: undefined } }));
        fetch();
      },
    };
  };
}
